import React, { useState, useEffect } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pagesApi } from '../services/api';
import ContactForm from '../components/ContactForm';
import NewsletterForm from '../components/NewsletterForm';
import SEOHead from '../components/SEOHead';

// Component registry - add new components here
const COMPONENTS = {
  'ContactForm': ContactForm,
  'Newsletter': NewsletterForm,
  // Add more components as needed:
  // 'FAQAccordion': FAQAccordion,
};

const DynamicPage = () => {
  const { t } = useTranslation();
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const { countryCode } = useLocalization();
  
  // Get slug from either URL param or pathname
  const slug = paramSlug || location.pathname.split('/')[1];
  
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchPage();
    }
  }, [slug, countryCode]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const data = await pagesApi.getBySlug(slug, countryCode);
      setPage(data);
      setError(null);
    } catch (err) {
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-16">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-white py-16">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-4xl font-serif text-[#2C2420] mb-4">Page Not Found</h1>
          <p className="text-gray-600">{error || 'The page you are looking for does not exist.'}</p>
        </div>
      </div>
    );
  }

  // Get the component to render if specified
  const ComponentToRender = page.component && COMPONENTS[page.component];

  return (
    <div className="min-h-screen bg-white py-16">
      <SEOHead title={page.title} url={`/${slug}`} />
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-serif text-[#2C2420] mb-8">{page.title}</h1>
        
        {/* Render static content */}
        {page.content && (
          <div 
            className="prose max-w-none text-gray-600 mb-8"
            dangerouslySetInnerHTML={{ __html: page.content.replace(/\n/g, '<br/>') }}
          />
        )}

        {/* Render dynamic component if specified */}
        {ComponentToRender && (
          <div className="mt-8">
            <ComponentToRender />
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicPage;
